import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChefHat, Euro, Wine, Plus, X } from "lucide-react";
import { CUISINE_OPTIONS, BUDGET_LEVELS, SAUCE_OPTIONS } from "./constants";

export const CuisineCard = ({ selected, onToggle }) => (
  <Card className="bg-card border-border" data-testid="cuisine-preferences-card">
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2">
        <ChefHat className="w-5 h-5 text-primary" />
        Cuisine Preferences
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CUISINE_OPTIONS.map((cuisine) => {
          const isActive = selected.includes(cuisine.id);
          return (
            <div
              key={cuisine.id}
              onClick={() => onToggle(cuisine.id)}
              className={`p-4 border rounded cursor-pointer transition-all ${
                isActive ? "border-primary bg-primary/30 ring-1 ring-primary/50" : "border-border hover:border-border"
              }`}
              data-testid={`cuisine-${cuisine.id}`}
            >
              <div className="flex items-center gap-3">
                <cuisine.icon className={`w-5 h-5 ${isActive ? "text-primary" : "text-muted-foreground"}`} />
                <span className={isActive ? "text-primary font-semibold" : "text-muted-foreground"}>
                  {cuisine.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </CardContent>
  </Card>
);

export const BudgetCard = ({ budgetLevel, budgetMax, onLevelChange, onMaxChange }) => (
  <Card className="bg-card border-primary/30" data-testid="budget-preferences-card">
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2">
        <Euro className="w-5 h-5 text-primary" />
        Budget (Optional)
        <Badge variant="outline" className="ml-2 text-primary border-primary">New</Badge>
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-6">
      <div>
        <p className="text-sm text-muted-foreground mb-4">Select your preferred price range</p>
        <div className="flex flex-wrap gap-3">
          {BUDGET_LEVELS.map((level) => {
            const isActive = budgetLevel === level.value;
            return (
              <div
                key={level.value}
                onClick={() => onLevelChange(isActive ? null : level.value)}
                className={`cursor-pointer p-4 rounded-xl border transition-all flex flex-col items-center min-w-[80px] ${
                  isActive ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                }`}
                data-testid={`budget-level-${level.value}`}
              >
                <span className={`text-xl font-semibold ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                  {level.label}
                </span>
                <span className="text-xs text-muted-foreground mt-1">{level.description}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <Label className="text-sm text-muted-foreground">Or specify your maximum budget per person</Label>
        <div className="flex items-center gap-2 mt-2">
          <Input
            type="number"
            placeholder="z.B. 150"
            value={budgetMax}
            onChange={(e) => onMaxChange(e.target.value)}
            className="bg-transparent border-border focus:border-primary w-32"
            data-testid="budget-max-input"
          />
          <span className="text-muted-foreground">€ pro Person</span>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const SauceCard = ({ selected, customSauces, onToggle, onCustomChange }) => {
  const [newSauce, setNewSauce] = useState("");

  const addCustom = () => {
    if (!newSauce.trim()) return;
    onCustomChange([...(customSauces || []), newSauce.trim()]);
    setNewSauce("");
  };

  return (
    <Card className="bg-card border-border" data-testid="sauce-preferences-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Wine className="w-5 h-5 text-primary" />
          Sauce Preferences
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">Select your favorites or add your own</p>
        <div className="flex flex-wrap gap-2">
          {SAUCE_OPTIONS.map((sauce) => (
            <Badge
              key={sauce}
              variant={selected.includes(sauce) ? "default" : "outline"}
              onClick={() => onToggle(sauce)}
              className={`cursor-pointer transition-all ${
                selected.includes(sauce)
                  ? "bg-primary text-primary-foreground hover:bg-primary/80"
                  : "border-border hover:border-primary"
              }`}
              data-testid={`sauce-${sauce}`}
            >
              {sauce}
            </Badge>
          ))}
          {(customSauces || []).map((sauce, index) => (
            <Badge key={`custom-${index}`} className="bg-primary/20 text-primary border-primary/30 flex items-center gap-1">
              {sauce}
              <button
                onClick={() => onCustomChange(customSauces.filter((_, i) => i !== index))}
                className="ml-1 hover:text-destructive"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Add custom sauce..."
            value={newSauce}
            onChange={(e) => setNewSauce(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && addCustom()}
            className="bg-transparent border-border focus:border-primary"
            data-testid="new-custom-sauce-input"
          />
          <Button variant="outline" onClick={addCustom} className="border-border hover:border-primary" data-testid="add-custom-sauce-button">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
